<template>
  <v-text-field
      :error-messages="error"
      :label="translation(translationList.customer_nr_ci, 'Număr')"
      v-model="nrCi"
      name="nr_ci"
      :rules="nrCiValidationRules"
      @input="onInput"
      variant="outlined"
      :tile="true"
  >
  </v-text-field>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, toRefs } from 'vue';
import { useTranslationStore, translation } from '~/plugins/store/translations';
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: String as () => string | null,
    customerIndex: Number as () => number | null,
  },
  setup(props, { emit }) {
    const { modelValue, customerIndex } = toRefs(props);
    const nrCi = ref(modelValue.value ?? '');
    const error = ref<string[]>([]);

    watch(nrCi, (newValue) => {
      const isValid = validateNrCi(newValue);
      emit('update:modelValue', newValue);
      if (customerIndex.value !== undefined) {
        emit('validation-customer-status', { field: `nr_ci-${customerIndex.value}`, isValid });
      } else {
        emit('validation-customer-status', { field: `nr_ci`, isValid });
      }
    });

    const validateNrCi = (value: string, showErrors?: boolean): boolean => {
      error.value = [];
      if (showErrors) {
        const errors = nrCiValidationRules
            .map(rule => rule(value))
            .filter(result => result !== true) as string[];
        if (errors && errors.length > 0) {
          error.value = errors;
          return false;
        }
      }
      return nrCiValidationRules.every(rule => rule(value) === true);
    };

    const translationList = ref<TranslationList>({});
    const translationStore = useTranslationStore();

    const nrCiValidationRules = [
      (v: string) => !!v || translation(translationList.value.required, 'Acest câmp este obligatoriu'),
      (v: string) => v.length >= 6 && v.length <= 7 || translation(translationList.value.invalid_field, 'Acest câmp este invalid.'),
      (v: string) => /^[0-9]+$/.test(v) || translation(translationList.value.invalid_field, 'Acest câmp este invalid.'),
    ];

    const onInput = (event: Event) => {
      const input = event.target as HTMLInputElement;
      nrCi.value = input.value;
    };

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      const isValid = validateNrCi(nrCi.value);
      if (customerIndex.value !== undefined) {
        emit('validation-customer-status', { field: `nr_ci-${customerIndex.value}`, isValid });
      } else {
        emit('validation-customer-status', { field: `nr_ci`, isValid });
      }

      // Listen for validate events
      window.addEventListener('validate-customer', handleValidationEvent);
      window.addEventListener('validate-travel', handleValidationEvent);
    });

    const handleValidationEvent = (data: any) => {
      if (data.detail && (data.detail === `nr_ci-${customerIndex.value}`
          || data.detail === `nr_ci`
          || data.detail === `number-${customerIndex.value}`
          || data.detail === `number`
      )) {
        const isValid = validateNrCi(nrCi.value, true);
        if (isValid) {
          emit('validation-customer-status', { field: data.detail, isValid });
        }
      }
    };

    watch(modelValue, (newValue) => {
      nrCi.value = newValue ?? '';
    });

    return {
      nrCi,
      nrCiValidationRules,
      translationList,
      translation,
      onInput,
      error,
    };
  },
});
</script>